import { acquireApiAccessToken, acquireGraphAccessToken } from "@timeghost/auth/acquire-token";
import { tryGetMsalInstance } from "@timeghost/auth/msal-instance";
import { createTimeghostWebApiStores, type TimeghostWebApiStores } from "@timeghost/web-api";
import { environment } from "@/lib/environment";
import { acquireTeamsApiToken, acquireTeamsGraphToken, isTeamsSsoActive } from "@/lib/teams/teams-sso";

type StoresRef = {
	current: TimeghostWebApiStores | null;
};

const storesRef: StoresRef = {
	current: null,
};

const resolveWorkspaceId = (): string | null => {
	const user = storesRef.current?.user.useStore.getState().value;
	const workspace = user?.workspace;
	if (!workspace || typeof workspace !== "object") {
		return null;
	}

	const workspaceId = (workspace as { readonly id?: unknown }).id;
	return typeof workspaceId === "string" ? workspaceId : null;
};

const resolvePrincipalId = (): string | null => {
	const user = storesRef.current?.user.useStore.getState().value;
	return typeof user?.id === "string" ? user.id : null;
};

const getAccessToken = async (): Promise<string | null> => {
	if (isTeamsSsoActive()) {
		return acquireTeamsApiToken();
	}

	const msal = tryGetMsalInstance();
	if (!msal) {
		return null;
	}

	return acquireApiAccessToken(msal, environment.VITE_AZURE_API_SCOPE);
};

const getGraphToken = async (): Promise<string | null> => {
	if (isTeamsSsoActive()) {
		return acquireTeamsGraphToken();
	}

	const msal = tryGetMsalInstance();
	if (!msal) {
		return null;
	}

	return acquireGraphAccessToken(msal, environment.VITE_AZURE_GRAPH_SCOPE);
};

export const getApiRequestHeaders = async (): Promise<Record<string, string>> => {
	const headers: Record<string, string> = {};
	const [accessToken, graphToken] = await Promise.all([getAccessToken(), getGraphToken()]);
	if (accessToken) {
		headers.Authorization = `Bearer ${accessToken}`;
	}
	if (graphToken) {
		headers["graph-token"] = graphToken;
	}
	const workspaceId = resolveWorkspaceId();
	if (workspaceId) {
		headers["workspace-id"] = workspaceId;
	}
	const principalId = resolvePrincipalId();
	if (principalId) {
		headers["principal-id"] = principalId;
	}
	return headers;
};

const stores = createTimeghostWebApiStores({
	baseUrl: environment.VITE_API_URL,
	auth: {
		getAccessToken,
		getGraphToken,
		getWorkspaceId: resolveWorkspaceId,
		getPrincipalId: resolvePrincipalId,
	},
});

storesRef.current = stores;

export default stores;
